import axios, { type AxiosError, type InternalAxiosRequestConfig } from 'axios';

import { getEnv } from '@core/config/env';
import { ORGANIZATION_HEADER, organizationStore } from '@core/org/organizationStore';

import { authEvents } from './authEvents';
import { toApiError } from './errors';
import { getAuthToken } from './tokenBridge';

type RetriableConfig = InternalAxiosRequestConfig & { _retried?: boolean };

export const api = axios.create({
  timeout: 20_000,
  headers: { Accept: 'application/json' },
});

api.interceptors.request.use(async (config) => {
  // baseURL is resolved per request rather than at module load, so importing this file
  // never forces window.env to be read before main.tsx has had a chance to validate it.
  config.baseURL ??= getEnv().GATEWAY || '/';

  // Which organization this request acts in. Absent means "the account's default", so a
  // fresh tab needs no setup. Re-read on a retry too: a switch can land between the
  // first attempt and the replay, and the replay must not act in the old organization.
  const organizationId = organizationStore.get();
  if (organizationId) {
    config.headers.set(ORGANIZATION_HEADER, organizationId);
  } else {
    config.headers.delete(ORGANIZATION_HEADER);
  }

  // On a 401 retry the response interceptor has already attached a freshly refreshed
  // token. Re-running getAuthToken() here would return the CACHED (still stale) one and
  // overwrite it, so the retry would 401 again and the refresh path could never succeed.
  if ((config as RetriableConfig)._retried) {
    return config;
  }

  const token = await getAuthToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * Single-flight refresh.
 *
 * When a token expires, every in-flight request 401s at once -- on the fleet screen that
 * is easily twenty. Without this guard each one calls `getToken({ skipCache: true })`
 * independently and hammers Clerk with twenty refreshes for one session.
 */
let refreshing: Promise<string | null> | null = null;

function refreshToken(): Promise<string | null> {
  refreshing ??= getAuthToken({ skipCache: true }).finally(() => {
    refreshing = null;
  });
  return refreshing;
}

api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const config = error.config as RetriableConfig | undefined;

    if (error.response?.status === 401 && config && !config._retried) {
      config._retried = true;
      const token = await refreshToken();

      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
        return api.request(config);
      }
      authEvents.emit('signed-out');
    }

    if (error.response?.status === 403) {
      authEvents.emit('forbidden', { path: config?.url });

      // Not a statement about this request but about the organization it acted in: it
      // was suspended, or the caller no longer has one. The bootstrap gate re-asks
      // `/users/me`, which is what turns this into the right full-screen state.
      const code = (error.response.data as { code?: unknown } | undefined)?.code;
      if (code === 'ORGANIZATION_SUSPENDED' || code === 'NO_ORGANIZATION') {
        authEvents.emit('organization-unavailable', { code, path: config?.url });
      }
    }

    // Nothing above this line ever sees a raw AxiosError.
    return Promise.reject(toApiError(error));
  },
);

/**
 * Clerk's getToken already caches, refreshes ahead of expiry and dedupes concurrent
 * calls, so the 401 path above is a safety net for clock skew and revoked sessions --
 * not the primary refresh mechanism. Do NOT add a timer-based refresh loop on top; it
 * will fight Clerk's own scheduling and produce spurious sign-outs.
 */
